import { createSelector } from '@reduxjs/toolkit';

import { getSearchedItemIds } from '@/redux/modules/search/search.selectors';
import { getPromotedSearchResults } from '@/redux/modules/searchPromoted.selectors';
import { GlobalState } from '@/redux/store';
import { blender } from '@/utils/blender';

// Search selectors can get very, very complex.
// Because of that, circular dependencies are going to be common.
// To avoid that, this file should contain selectors that pull from multiple slices.
// When it's all minified it would run fine, since everything's in one file,
// but test runs exploderate. 💥

const idSelector = (_: GlobalState, id: number | boolean | string) => id;

export const getSearchedAndPromotedItems = createSelector(
    [idSelector, getSearchedItemIds, getPromotedSearchResults],
    (columns: number, itemIds, promotedItemIds) => blender(itemIds, promotedItemIds, columns)
);

// Search-party returns more promoted lots than the SearchPage grid can display:
// the blender only emits enough chunks to alternate with the main rows it has
// to interleave, dropping any leftover trailing promoted chunks. Analytics
// should match what the user actually sees with the Featured badge, not the
// raw server-side list.
const SEARCH_PAGE_GRID_COLUMNS = 4;

/**
 * Returns the subset of `state.searchPromoted.itemIds` that actually reaches the SRP grid
 * after the blender row-pattern interleave. Use this for Featured analytics so the wire
 * value matches the cards rendered with the Featured badge.
 *
 * `itemIds` is taken as an argument (rather than read from `state.search.itemIds`) because
 * tracking inside the `submitSearch` thunk fires before `submitSearch.fulfilled` updates
 * the search slice — passing the fresh payload's `itemIDs` keeps both branches accurate.
 */
export const getDisplayedPromotedItemIds = (state: GlobalState, itemIds: number[]): number[] => {
    const promotedItemIds = getPromotedSearchResults(state);
    if (promotedItemIds.length === 0) {
        return promotedItemIds;
    }
    const blended = new Set(blender(itemIds, promotedItemIds, SEARCH_PAGE_GRID_COLUMNS));
    return promotedItemIds.filter((id) => blended.has(id));
};
